import { useEffect, useCallback, useRef } from 'react';
import { useTheme } from 'next-themes';
import { supabase } from '@/integrations/supabase/client';

/**
 * Theme hook with three modes:
 * - "light"  → always light (no schedule)
 * - "dark"   → always dark (no schedule)
 * - "auto"   → DARK 6 PM–5:59 AM, LIGHT 6 AM–5:59 PM (local device time)
 */
export const useAutoTheme = () => {
  const { theme, setTheme: setNextTheme } = useTheme();
  const preferenceRef = useRef(theme);

  useEffect(() => {
    preferenceRef.current = theme;
  }, [theme]);

  const applyTheme = useCallback(() => {
    const pref = preferenceRef.current;
    const hour = new Date().getHours();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (pref === 'light') {
      console.log(`🎨 [Theme] Manual LIGHT — hour: ${hour}, tz: ${tz}`);
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
      return;
    }

    if (pref === 'dark') {
      console.log(`🎨 [Theme] Manual DARK — hour: ${hour}, tz: ${tz}`);
      document.documentElement.classList.remove('light');
      document.documentElement.classList.add('dark');
      return;
    }

    // "auto" mode: dark 6PM–6AM, light 6AM–6PM
    const shouldBeDark = hour >= 18 || hour < 6;
    console.log(`🎨 [Theme] Auto mode — hour: ${hour}, tz: ${tz}, dark: ${shouldBeDark}`);
    document.documentElement.classList.toggle('dark', shouldBeDark);
    document.documentElement.classList.toggle('light', !shouldBeDark);
  }, []);

  // Load saved preference from DB
  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('theme_preference')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profile?.theme_preference) {
        setNextTheme(profile.theme_preference);
        preferenceRef.current = profile.theme_preference;
        setTimeout(applyTheme, 0);
      }
    };
    load();
  }, [setNextTheme, applyTheme]);

  // Re-evaluate every 60s + on foreground
  useEffect(() => {
    applyTheme();
    const interval = setInterval(applyTheme, 60_000);
    const onVis = () => {
      if (document.visibilityState === 'visible') applyTheme();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [applyTheme]);

  // Re-apply when preference changes
  useEffect(() => {
    applyTheme();
  }, [theme, applyTheme]);

  // Save preference
  const saveThemePreference = useCallback(async (newTheme: string) => {
    console.log(`🎨 [Theme] User selected: ${newTheme}`);
    preferenceRef.current = newTheme;
    setNextTheme(newTheme);
    setTimeout(applyTheme, 0);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase
      .from('profiles')
      .update({ theme_preference: newTheme })
      .eq('user_id', user.id);
  }, [setNextTheme, applyTheme]);

  return { theme, setTheme: saveThemePreference };
};
